import { z } from 'zod';
import { IdSchema, E164Schema, MoneySchema, TimestampSchema, ListQuerySchema } from './common.js';

export const ExtensionStatusSchema = z.enum(['active', 'disabled']);

/**
 * Note what is NOT here: no ha1, no password, no password_enc. SIP secrets never
 * appear in an extension DTO. Revealing one is a separate, step-up-gated,
 * audited endpoint (doc 06 section 4.4) -- keeping them out of this schema means
 * Fastify's schema-driven serialisation cannot leak them even by accident
 * (doc 05 section 11 step 12).
 */
export const ExtensionSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  site_id: IdSchema.nullable(),
  user_id: IdSchema.nullable(),
  number: z.string(),
  display_name: z.string(),
  status: ExtensionStatusSchema,
  outbound_caller_id_number: E164Schema.nullable(),
  outbound_caller_id_name: z.string().nullable(),
  call_timeout_sec: z.number().int(),
  max_concurrent_calls: z.number().int(),
  do_not_disturb: z.boolean(),
  forward_always: z.string().nullable(),
  follow_me: z.array(z.object({
    destination: z.string(), ring_sec: z.number(),
  })).nullable().optional(),
  follow_me_after_sec: z.number().nullable().optional(),
  follow_me_caller_id: z.string().optional(),
  forward_busy: z.string().nullable(),
  forward_no_answer: z.string().nullable(),
  directory_visible: z.boolean(),
  language: z.string().nullable(),

  // ---- identity & presentation
  avatar_url: z.string().nullable(),
  hide_caller_id: z.boolean(),
  emergency_caller_id_number: E164Schema.nullable(),

  // ---- cost attribution
  account_code: z.string().nullable(),
  admin_group_id: IdSchema.nullable(),
  /** Null means inherit from the cost centre, then the account. */
  dial_policy_id: IdSchema.nullable().optional(),
  admin_group_name: z.string().nullable().optional(),
  budget_amount: MoneySchema.nullable(),
  budget_period: z.enum(['daily', 'weekly', 'monthly']).nullable(),

  // ---- supervision & security
  prevent_spy: z.boolean(),
  require_authentication: z.boolean(),

  // ---- media capability
  fax_enabled: z.boolean(),
  video_enabled: z.boolean(),
  phone_engine: z.enum(['external', 'webrtc', 'mobile', 'softphone']),

  // ---- recording policy (doc 22: RICA/POPIA obligations differ by role)
  record_mode: z.enum(['none', 'all', 'inbound', 'outbound', 'on_demand']),
  record_retention_days: z.number().int().nullable(),

  // ---- outbound dialling restrictions
  /**
   * Other extensions only, enforced on the switch. Everything else a seat may
   * dial comes from its dial policy: dial_policy_id, or its cost centre's, or
   * the account's.
   */
  internal_only: z.boolean(),

  extension_type_id: IdSchema.nullable(),
  extension_type_name: z.string().nullable().optional(),
  /** Whether its type includes queue membership: only these may be put in a queue (docs/29). */
  may_queue: z.boolean().optional(),

  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  version: z.number().int(),
  /**
   * Live registration, as last reported by the node. ABSENT when no node has
   * reported at all, which is different from "not registered".
   */
  registration: z
    .object({
      registered: z.boolean(),
      contact: z.string().nullable(),
      contact_ip: z.string().nullable(),
      contact_port: z.number().int().nullable(),
      transport: z.string().nullable(),
      user_agent: z.string().nullable(),
      ping_status: z.string().nullable(),
      /**
       * Presence, not a snapshot.
       *
       * The row survives going offline, so every device field above is the
       * LAST KNOWN value once `registered` is false -- which is the whole
       * point: an offline extension is when someone actually needs to know
       * where it last registered from.
       */
      last_seen_at: TimestampSchema.nullable(),
      offline_since: TimestampSchema.nullable(),
      first_seen_at: TimestampSchema.nullable(),
      expires_at: TimestampSchema.nullable(),
      /** How current the answer is. A stale snapshot must not read as live. */
      snapshot_at: TimestampSchema,
    })
    .optional(),
});
export type Extension = z.infer<typeof ExtensionSchema>;

/**
 * Extension numbers are validated against the SAME rule the dialplan uses.
 * 91xx is reserved for service codes (echo 9196, tone 9195); allowing an
 * extension there would create a number that can be created but never dialled,
 * because the dialplan matches the service code first.
 */
export const EXTENSION_NUMBER_RE = /^(?!91\d\d$)\d{2,6}$/;

export const ExtensionCreateSchema = z.object({
  extension_type_id: IdSchema.optional(),
  number: z.string().regex(EXTENSION_NUMBER_RE, 'must be 2-6 digits and not in the reserved 91xx service range'),
  display_name: z.string().min(1).max(200),
  site_id: IdSchema.optional(),
  user_id: IdSchema.optional(),
  outbound_caller_id_number: E164Schema.optional(),
  outbound_caller_id_name: z.string().max(200).optional(),
  call_timeout_sec: z.number().int().min(5).max(300).default(30),
  max_concurrent_calls: z.number().int().min(1).max(100).default(4),
  directory_visible: z.boolean().default(true),
  language: z.string().max(16).optional(),
}).strict();
export type ExtensionCreate = z.infer<typeof ExtensionCreateSchema>;

/**
 * Creating a block of seats in one go: 1010 through 1029.
 *
 * A separate schema rather than an array of ExtensionCreate, because the shape
 * of the request is genuinely different. What varies between the seats is only
 * the NUMBER; everything else -- the product, the cost centre, whether they may
 * dial out -- is one decision applied to the whole block. Sending twenty nearly
 * identical objects would put the caller in charge of keeping them identical.
 *
 * `from` and `to` are strings, not integers, so that a range starting at 0100
 * keeps its width. Parsing them as numbers turns 0101 into 101, which is a
 * different extension and in most numbering plans a taken one.
 */
export const ExtensionRangeCreateSchema = z.object({
  from: z.string().regex(/^[0-9]{2,6}$/, 'must be 2-6 digits'),
  to: z.string().regex(/^[0-9]{2,6}$/, 'must be 2-6 digits'),
  /** Display names become "<prefix> 1010"; the bare number when omitted. */
  display_name_prefix: z.string().max(80).optional(),
  extension_type_id: IdSchema.optional(),
  admin_group_id: IdSchema.optional(),
  internal_only: z.boolean().optional(),
  call_timeout_sec: z.number().int().min(5).max(300).default(30),
  max_concurrent_calls: z.number().int().min(1).max(100).default(4),
  directory_visible: z.boolean().default(true),
}).strict();
export type ExtensionRangeCreate = z.infer<typeof ExtensionRangeCreateSchema>;

/**
 * The ceiling on one request.
 *
 * Not a product limit -- a blast radius. Every seat mints a SIP credential, and
 * the response carries every one of those passwords back exactly once, so a
 * mistyped range must not turn into ten thousand credentials nobody captured.
 */
export const EXTENSION_RANGE_MAX = 100;

/**
 * The codecs a seat may negotiate.
 *
 * Exactly what this build advertises: vars.xml sets
 * `global_codec_prefs=PCMA,PCMU,OPUS,G722`. G.729 is compiled (mod_bcg729) but
 * `arc_fs_enable_g729` defaults to false and it is left out of that list, so
 * offering it here would be a portal control the switch never honours. Adding
 * it later is one value here and one in the CHECK on extensions.allowed_codecs.
 */
export const CodecSchema = z.enum(['PCMA', 'PCMU', 'G722', 'OPUS']);

export const ExtensionUpdateSchema = ExtensionCreateSchema.partial().extend({
  /**
   * NULL clears the override and returns the seat to the switch's preference.
   * An empty array is refused rather than treated as "none": a seat with no
   * codecs cannot complete a call, and nobody means that.
   */
  allowed_codecs: z.array(CodecSchema).min(1).max(4).nullable().optional(),
  status: ExtensionStatusSchema.optional(),
  do_not_disturb: z.boolean().optional(),
  forward_always: z.string().max(128).nullable().optional(),
  forward_busy: z.string().max(128).nullable().optional(),
  forward_no_answer: z.string().max(128).nullable().optional(),

  /**
   * Ring these in order after the seat itself does not answer.
   *
   * The column has existed since the first telephony migration and the portal
   * has displayed it read-only ever since -- but there was no way to SET it
   * through the API and nothing on the switch consulted it, so "follow me" was
   * a feature in name only.
   *
   * `ring_sec` is how long to ring THAT step, not a delay before it. A step of
   * zero would ring forever, which is why the minimum is not zero.
   */
  follow_me: z.array(z.object({
    destination: z.string().min(2).max(128),
    ring_sec: z.number().int().min(5).max(120).default(20),
  })).max(5).nullable().optional(),

  /**
   * How long the seat rings before follow-me starts.
   *
   * Separate from call_timeout_sec on purpose: turning follow-me on usually
   * means wanting a SHORTER first leg, and reusing one column would make that
   * change ordinary ringing too. Null falls back to call_timeout_sec.
   */
  follow_me_after_sec: z.number().int().min(5).max(120).nullable().optional(),

  /**
   * Whose number the next phone in the chain sees.
   *
   * A preference on-net; a correctness matter off-net, where a carrier will
   * reject or rewrite a CLI belonging to somebody you do not own.
   */
  follow_me_caller_id: z.enum(['original', 'extension']).optional(),

  avatar_url: z.string().max(500).nullable().optional(),
  hide_caller_id: z.boolean().optional(),
  emergency_caller_id_number: E164Schema.nullable().optional(),

  account_code: z.string().max(32).nullable().optional(),
  admin_group_id: IdSchema.nullable().optional(),
  dial_policy_id: IdSchema.nullable().optional(),
  budget_amount: MoneySchema.nullable().optional(),
  budget_period: z.enum(['daily', 'weekly', 'monthly']).nullable().optional(),

  prevent_spy: z.boolean().optional(),
  require_authentication: z.boolean().optional(),

  fax_enabled: z.boolean().optional(),
  video_enabled: z.boolean().optional(),
  phone_engine: z.enum(['external', 'webrtc', 'mobile', 'softphone']).optional(),

  record_mode: z.enum(['none', 'all', 'inbound', 'outbound', 'on_demand']).optional(),
  record_retention_days: z.number().int().min(1).max(3650).nullable().optional(),

  internal_only: z.boolean().optional(),

  extension_type_id: IdSchema.nullable().optional(),
}).strict();
export type ExtensionUpdate = z.infer<typeof ExtensionUpdateSchema>;

export const ExtensionListQuerySchema = ListQuerySchema.extend({
  /**
   * Up to a thousand, where most lists stop at two hundred: picking the members
   * of a hunt list of five hundred needs everybody in view at once (docs/26 D5).
   */
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  status: ExtensionStatusSchema.optional(),
  site_id: IdSchema.optional(),
  q: z.string().max(200).optional(),
  sort: z.enum(['number', '-number', 'display_name', '-display_name', 'created_at', '-created_at'])
    .default('number'),
});

/** Response of the step-up gated reveal endpoint. Shown once, never listed. */
export const SipCredentialRevealSchema = z.object({
  username: z.string(),
  realm: z.string(),
  password: z.string(),
  proxy: z.string(),
});
export type SipCredentialReveal = z.infer<typeof SipCredentialRevealSchema>;


// ------------------------------------------------------------ outbound routes

/**
 * Which trunk carries a call, and what the carrier is shown.
 *
 * A route is selected by LONGEST MATCHING PREFIX, then by priority. The length
 * rule comes first and is not configurable: a route on '2782' has to beat one
 * on '27' or the specific rule somebody added for mobiles is shadowed by the
 * general one and never fires. Priority only breaks ties between prefixes of
 * equal length.
 */
export const OutboundRouteSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema.nullable(),
  name: z.string(),
  match_prefix: z.string(),
  destination_class_id: IdSchema.nullable(),
  priority: z.number().int(),
  trunk_selection: z.string(),
  trunk_ids: z.array(IdSchema).nullable(),
  strip_digits: z.number().int(),
  prepend: z.string().nullable(),
  is_enabled: z.boolean(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type OutboundRoute = z.infer<typeof OutboundRouteSchema>;

/**
 * `trunk_selection` accepts 'ordered' and 'fixed' only.
 *
 * The column also permits 'lcr', and the node does not implement it -- the
 * generated dialplan takes the first usable trunk in `trunk_ids` order,
 * whatever this says. Accepting a value the switch silently reinterprets is how
 * a routing screen ends up lying about which carrier carried a call, so it is
 * refused at the edge until least-cost routing has per-trunk rates to work
 * from.
 */
export const TRUNK_SELECTION = ['ordered', 'fixed'] as const;

export const OutboundRouteCreateSchema = z.object({
  name: z.string().min(1).max(120),
  /** Digits only. The generated pattern asserts these against the dialled number. */
  match_prefix: z.string().regex(/^[0-9]{1,15}$/, 'must be 1-15 digits, no + or spaces'),
  destination_class_id: IdSchema.nullish(),
  priority: z.number().int().min(1).max(9999).default(100),
  trunk_selection: z.enum(TRUNK_SELECTION).default('ordered'),
  /** Tried in order; the first active outbound-capable one carries the call. */
  trunk_ids: z.array(IdSchema).min(1).max(10),
  strip_digits: z.number().int().min(0).max(15).default(0),
  prepend: z.string().regex(/^[0-9+]{0,15}$/).nullish(),
  is_enabled: z.boolean().default(true),
}).strict();
export type OutboundRouteCreate = z.infer<typeof OutboundRouteCreateSchema>;

export const OutboundRouteUpdateSchema = OutboundRouteCreateSchema.partial();
export type OutboundRouteUpdate = z.infer<typeof OutboundRouteUpdateSchema>;

/**
 * An account's outbound routing as a whole (migration 0045).
 *
 * The default route is the carriers every outside call leaves by, in order: one
 * is "everything via this trunk", more is failover. Prefix routes apply on top
 * only while prefix_routing is on; switching it off keeps them, unused.
 */
export const OutboundRoutingUpdateSchema = z.object({
  default_trunk_ids: z.array(IdSchema).min(1).max(10),
  prefix_routing: z.boolean(),
}).strict();
export type OutboundRoutingUpdate = z.infer<typeof OutboundRoutingUpdateSchema>;
